import clsx from 'clsx'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { useEffect, type ReactNode } from 'react'
import { View } from 'react-native'
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { scheduleOnRN } from 'react-native-worklets'

import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'
import { useRoutes } from '@/src/general/utils/routes'

import { Card, CardBody, CardHeader } from '../card'
import { Icon } from '../media/Icon'
import { Typography } from '../text/Typography'
import type { TStreak } from './streak.type'

// Let the screen settle before the flame pops, so the user has time to see it
const FLAME_ANIMATION_DELAY_MS = 500

const markStreakAnimationAsSeen = () => {
  storage.setItem(STORAGE_KEYS.WATCHMAN_STREAK_ANIMATED_TODAY, dayjs().format('YYYY-MM-DD'))
}

const StreakStatus = ({
  level,
  shouldAnimateFlame,
  isToday,
}: {
  level: number
  shouldAnimateFlame: boolean
  isToday: boolean
}) => {
  const scale = useSharedValue(1)
  const rotate = useSharedValue(0)

  useEffect(() => {
    if (!shouldAnimateFlame || level !== 3) return

    // Reset to initial state before animating
    scale.value = 0.3
    rotate.value = 0

    // Step 1: linear 150ms → scale(0.89) rotate(23.645deg)
    scale.value = withDelay(FLAME_ANIMATION_DELAY_MS, withTiming(0.89, { duration: 150, easing: Easing.linear }))
    rotate.value = withDelay(
      FLAME_ANIMATION_DELAY_MS,
      withTiming(23.645, { duration: 150, easing: Easing.linear }, (step1Finished) => {
        'worklet'
        if (!step1Finished) return

        // Step 2: ease-out 250ms → scale(1.175) rotate(-9.2deg)
        scale.value = withTiming(1.175, {
          duration: 250,
          easing: Easing.out(Easing.ease),
        })
        rotate.value = withTiming(-9.2, { duration: 250, easing: Easing.out(Easing.ease) }, (step2Finished) => {
          'worklet'
          if (!step2Finished) return

          // Step 3: spring physics → scale(1) rotate(0deg)
          scale.value = withSpring(1, {
            stiffness: 1090,
            damping: 24,
            mass: 1,
          })
          rotate.value = withSpring(0, { stiffness: 1090, damping: 24, mass: 1 }, (step3Finished) => {
            'worklet'
            // Only mark as seen when fully played, so an interrupted animation is replayed next time
            if (step3Finished) scheduleOnRN(markStreakAnimationAsSeen)
          })
        })
      }),
    )

    return () => {
      cancelAnimation(scale)
      cancelAnimation(rotate)
    }
  }, [shouldAnimateFlame, scale, rotate, level])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }, { rotate: `${rotate.value}deg` }],
  }))

  return (
    <View
      className={clsx('rounded-full relative flex items-center justify-center ', {
        'border-none h-10 w-10': level === 3,
        'bg-secondary-bg-high': level === 3 && !isToday,
        // Today's blue streak background only comes from the bar connecting to a completed yesterday
        'bg-transparent': level === 3 && isToday,
        'bg-secondary-bg-medium h-8 w-8  border-1 border-secondary-bg-high': level !== 3,
      })}
      testID={
        level === 3 && isToday ? TEST_ID.WATCHMAN.INTENTIONS.STREAK_FLAME : TEST_ID.WATCHMAN.INTENTIONS.STREAK(level)
      }
    >
      {level > 0 && !(level === 3 && isToday) && (
        <View
          className={clsx('rounded-full bg-secondary-button-solid flex justify-center items-center', {
            'h-8 w-8': level === 3,
            'h-5 w-5': level === 2,
            'h-3 w-3': level === 1,
          })}
        >
          {level === 3 && <Icon name={ICON.CHECK} size="md" className="text-white" />}
        </View>
      )}
      {level === 3 && isToday && (
        <View className={clsx('bg-transparent flex justify-center items-center absolute bottom-1')}>
          <Animated.View style={animatedStyle}>
            <Icon name={ICON.STREAK_FLAME} size="3xl" className="text-white" />
          </Animated.View>
        </View>
      )}
    </View>
  )
}

export type TStreakBaseProps = TStreak & {
  footer?: ReactNode
  title: ReactNode
}

export const StreakBase = ({ days, footer, title }: TStreakBaseProps) => {
  const shouldAnimateFlame = days.at(-1)?.shouldAnimateFlame ?? false
  const router = useRouter()
  const { routes } = useRoutes()
  const handlePress = () => {
    router.navigate(routes.watchmanIntentions())
  }

  return (
    <Card
      isPressable={!footer}
      onPress={footer ? undefined : handlePress}
      className="flex w-full flex-col items-center justify-between gap-2 rounded-xl bg-secondary-bg-light p-4 shadow-sm min-w-80 sm:max-w-[450px] sm:self-center"
      testID={TEST_ID.WATCHMAN.INTENTIONS.STREAK_CARD}
    >
      <CardHeader className="flex w-full flex-row items-start gap-2 p-0">
        <Icon name={ICON.LANTERN} size="sm" className="text-secondary-text-contrast-low" />
        <Typography colors="secondary" bold className="flex-1">
          {title}
        </Typography>
      </CardHeader>
      <CardBody className="p-0 m-auto w-full max-w-[450px] flex-row items-center justify-start overflow-visible">
        {days[0]?.connectLeft && <View className={clsx('absolute h-10 w-10 -left-5 bottom-0 bg-secondary-bg-high')} />}
        {days.map((day, index) => (
          <View key={day.key} className={clsx('gap-1', { 'flex-1': !day.isToday })}>
            {day.connectRight && (
              <View
                className={clsx('flex flex-row absolute h-10 w-full left-0 bottom-0', {
                  'animate-expand-width': shouldAnimateFlame && index === days.length - 2,
                })}
              >
                <View className="absolute w-full h-10 left-5 bottom-0 bg-secondary-bg-high">
                  <View className="w-5 absolute -right-5 h-10 bottom-0 bg-secondary-bg-high rounded-r-full" />
                </View>
              </View>
            )}
            <View
              className={clsx({ 'w-10': day.level === 3, 'w-8': day.level !== 3 }, 'flex items-center justify-center')}
            >
              <Typography
                className={day.isToday ? 'text-secondary-text-contrast-default' : 'text-secondary-text-placeholder'}
                center
                bold={day.isToday}
                size="sm"
              >
                {day.label}
              </Typography>
            </View>
            <View className="w-10 h-10 flex items-start justify-center">
              <StreakStatus level={day.level} shouldAnimateFlame={shouldAnimateFlame} isToday={day.isToday} />
            </View>
          </View>
        ))}
      </CardBody>
      {footer}
    </Card>
  )
}
